import { useAuth } from "../contexts/AuthContext"
import type { GiftList, Item, PublicUser } from "../types"
import { useState, useEffect } from "react"
import { Link, Navigate, useParams } from "react-router-dom"
import NavBar from "../components/NavBar"
import ItemRow from "../components/ItemRow"
import { fetchUser } from "../api/users"
import { fetchUserList, purchaseItem } from "../api/lists"

export default function UserListPage() {
  const { currentUser } = useAuth()
  const { id } = useParams()
  const [user, setUser] = useState<PublicUser | null>(null)
  const [list, setList] = useState<GiftList<Item> | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let active = true
    Promise.all([fetchUser(id || ""), fetchUserList<Item>(id || "")])
      .then(([userData, listData]) => {
        if (!active) return
        setUser(userData)
        setList(listData)
      })
      .catch(() => {
        if (active) setError("Unable to load this list. Please try again.")
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [id])

  async function handlePurchase(itemId: number) {
    if (!list) throw new Error("List is not available")
    const updated = await purchaseItem(list.id, itemId)
    setList((prev) =>
      prev
        ? {
            ...prev,
            items: prev.items.map((i) => (i.id === itemId ? updated : i)),
          }
        : prev
    )
  }

  function renderListContent() {
    if (loading) {
      return <p className="text-sm text-gray-500">Loading...</p>
    }
    if (error) {
      return <p className="text-sm text-red-500">{error}</p>
    }
    if (!list) {
      return (
        <div className="space-y-3">
          <p className="text-sm text-gray-500">
            This user doesn't have a list yet.
          </p>
        </div>
      )
    }
    if (list.items.length === 0) {
      return (
        <div className="space-y-3">
          <p className="text-sm text-gray-500">No items on this list yet.</p>
        </div>
      )
    }
    return (
      <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3 items-start">
        {list.items.map((item) => (
          <ItemRow key={item.id} item={item} onPurchase={handlePurchase} />
        ))}
      </div>
    )
  }

  if (String(currentUser?.id) === id) return <Navigate to="/dashboard" replace />

  return (
    <div className="min-h-screen bg-gray-50">
      <NavBar />

      <main className="max-w-2xl lg:max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-6">
          <Link
            to="/users"
            className="inline-flex items-center min-h-11 px-3 text-sm text-gray-500 hover:text-gray-900 transition-colors"
          >
            ← All lists
          </Link>
          {user && (
            <h2 className="text-lg font-semibold text-gray-900">
              {user.name}'s List
            </h2>
          )}
        </div>

        {renderListContent()}
      </main>
    </div>
  )
}
